import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCcw, ReceiptText, PackageSearch, ShieldCheck } from 'lucide-react';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { adminListOrders, adminReconcileOrder } from '../lib/api';

const formatINR = (rupees) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(rupees || 0);

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const STATUS_CLASSES = {
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  created: 'bg-amber-100 text-amber-900 border-amber-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
};

const OrdersTab = ({ passcode }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconcilingId, setReconcilingId] = useState(null);

  const load = () => {
    setLoading(true);
    adminListOrders(passcode)
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Could not load orders.'))
      .finally(() => setLoading(false));
  };

  const handleReconcile = async (order) => {
    setReconcilingId(order.id);
    try {
      const updated = await adminReconcileOrder(passcode, order.id);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...updated } : o)));
      if (updated.status === 'paid') {
        toast.success('Payment verified with Razorpay', { description: 'Order marked paid and receipt emailed to the customer.' });
      } else {
        toast('No payment captured yet at Razorpay', {
          description: 'The order will remain in \u201ccreated\u201d until Razorpay reports a captured payment.',
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not verify payment.');
    } finally {
      setReconcilingId(null);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-[#5a5a5a]">Showing {orders.length} recent orders (paid, created &amp; failed).</p>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#EDE3CC] transition-colors text-xs">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#5a5a5a]"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : !orders.length ? (
        <div className="text-center py-16 text-[#5a5a5a] bg-white border border-dashed border-[#E8DCC0] rounded-2xl">
          <PackageSearch className="w-10 h-10 mx-auto mb-3 text-[#A36B1F]" strokeWidth={1.5} />
          <p className="font-medium text-[#0F1B2D]">No orders yet.</p>
          <p className="text-sm mt-1">Orders will show here as soon as customers complete checkout.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {orders.map((o) => {
            const amt = (o.amount_paise || 0) / 100;
            const cls = STATUS_CLASSES[o.status] || 'bg-gray-100 text-gray-700 border-gray-200';
            return (
              <article key={o.id} className="bg-white border border-[#E8DCC0] rounded-2xl p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h3 className="font-serif-besley text-lg text-[#0F1B2D] truncate">{o.item_name}</h3>
                    <p className="text-xs text-[#5a5a5a] mt-1 capitalize">{o.order_type} \u00b7 {formatDate(o.paidAt || o.createdAt)}</p>
                  </div>
                  <Badge className={`shrink-0 border ${cls}`}>{o.status}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm text-[#3a3a3a] mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#5a5a5a]">Customer</p>
                    <p className="text-[#0F1B2D] font-medium truncate">{o.customer_name}</p>
                    <a className="text-xs text-[#A36B1F] underline" href={`mailto:${o.customer_email}`}>{o.customer_email}</a>
                    <p className="text-xs">{o.customer_phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-[#5a5a5a]">Amount</p>
                    <p className="font-serif-besley text-2xl text-[#0F1B2D]">{formatINR(amt)}</p>
                  </div>
                </div>
                <div className="border-t border-[#F0E7CE] pt-3 text-xs text-[#5a5a5a] flex items-center gap-2 flex-wrap">
                  <ReceiptText className="w-3.5 h-3.5" />
                  <span className="font-mono truncate">{o.razorpay_payment_id || o.razorpay_order_id || o.id}</span>
                  {o.status === 'created' && (
                    <button
                      onClick={() => handleReconcile(o)}
                      disabled={reconcilingId === o.id}
                      className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#EDE3CC] transition-colors text-xs disabled:opacity-60"
                    >
                      {reconcilingId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      Verify payment
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersTab;
